import {
  ArrowDown,
  ArrowUpRight,
  Award,
  BookOpen,
  Brain,
  Compass,
  Flame,
  Footprints,
  HeartHandshake,
  Mountain,
  ShieldCheck,
  Sparkles,
  Target,
  UsersRound,
} from "lucide-react";
import Header from "@/reusable_sections/Header";
import Footer from "@/reusable_sections/Footer";
import { assetUrl, homeUrl } from "@/lib/assets";

type GrowthPillar = {
  number: string;
  title: string;
  subtitle: string;
  description: string;
  icon: typeof BookOpen;
};

type AgeRoute = {
  number: string;
  title: string;
  ages: string;
  motto: string;
  description: string;
  image: string;
  alt: string;
};

const growthPillars: GrowthPillar[] = [
  {
    number: "01",
    title: "Espiritual",
    subtitle: "Favor con Dios",
    description:
      "Una relación viva con Jesucristo mediante la Biblia, la oración, el discipulado y el servicio.",
    icon: BookOpen,
  },
  {
    number: "02",
    title: "Físico",
    subtitle: "Estatura y destreza",
    description:
      "Campismo, aventura, primeros auxilios y hábitos saludables que desarrollan seguridad y perseverancia.",
    icon: Mountain,
  },
  {
    number: "03",
    title: "Mental",
    subtitle: "Sabiduría y carácter",
    description:
      "Decisiones responsables, habilidades prácticas, disciplina personal y aprendizaje para toda la vida.",
    icon: Brain,
  },
  {
    number: "04",
    title: "Social",
    subtitle: "Favor con las personas",
    description:
      "Trabajo en equipo, mentoría, respeto, liderazgo de servicio y compromiso con la comunidad.",
    icon: UsersRound,
  },
];

const ageRoutes: AgeRoute[] = [
  {
    number: "01",
    title: "Aventureros",
    ages: "5–7 años",
    motto: "Descubrir",
    description:
      "Primeros pasos de fe, amistad y exploración mediante experiencias seguras, alegres y apropiadas para su edad.",
    image:
      assetUrl("royal_rangers_aventureros_kids.png"),
    alt: "Niños pequeños participando en actividades de Aventureros",
  },
  {
    number: "02",
    title: "Exploradores",
    ages: "8–10 años",
    motto: "Aprender",
    description:
      "Destrezas prácticas, campamentos iniciales y desafíos que fortalecen la responsabilidad y el trabajo en equipo.",
    image:
      assetUrl("royal_rangers_exploradores_outdoor.png"),
    alt: "Exploradores aprendiendo juntos al aire libre",
  },
  {
    number: "03",
    title: "Expedicionarios",
    ages: "11–13 años",
    motto: "Avanzar",
    description:
      "Aventura, superación personal y fundamentos bíblicos para acompañar una etapa decisiva de crecimiento.",
    image:
      assetUrl("royal_rangers_expedicionarios_camping.png"),
    alt: "Expedicionarios desarrollando habilidades de campamento",
  },
  {
    number: "04",
    title: "Colonos",
    ages: "14–17 años",
    motto: "Liderar",
    description:
      "Liderazgo de servicio, misión y mentoría para preparar jóvenes firmes en su fe y listos para influir con propósito.",
    image:
      assetUrl("royal_rangers_colonos_leadership.png"),
    alt: "Jóvenes de Colonos participando en una experiencia de liderazgo",
  },
];

const experiences = [
  {
    title: "Aventura al aire libre",
    description: "Senderismo, orientación, campismo y desafíos que despiertan curiosidad y confianza.",
    icon: Compass,
  },
  {
    title: "Discipulado bíblico",
    description: "Verdades de la Palabra llevadas a la vida cotidiana por medio de mentores cercanos.",
    icon: BookOpen,
  },
  {
    title: "Liderazgo práctico",
    description: "Decisiones, responsabilidad y trabajo en equipo aprendidos mientras se ponen en acción.",
    icon: Award,
  },
  {
    title: "Servicio y misión",
    description: "Compasión concreta para amar a la iglesia, la familia y la comunidad.",
    icon: HeartHandshake,
  },
  {
    title: "Carácter y disciplina",
    description: "Hábitos de integridad, constancia y perseverancia que permanecen para toda la vida.",
    icon: ShieldCheck,
  },
  {
    title: "Hermandad cristiana",
    description: "Amistades sanas y líderes comprometidos que acompañan a cada explorador.",
    icon: UsersRound,
  },
];

export default function KingExplorersPage() {
  return (
    <div className="acad-react-page acad-ker-page">
      <Header />

      <main>
        <section className="acad-ker-hero" data-nav="dark">
          <img
            className="acad-ker-hero-image"
            src={assetUrl("exploradores_del_rey_campground_activity.png")}
            alt="Niños y jóvenes de Exploradores del Rey compartiendo una aventura al aire libre"
            loading="eager"
            decoding="async"
          />
          <span className="acad-ker-hero-shade" />
          <span className="acad-ker-hero-lines" aria-hidden="true" />

          <div className="acad-ker-shell acad-ker-hero-inner">
            <div className="acad-ker-hero-topline">
              <span className="acad-ker-mark"><Compass aria-hidden="true" /></span>
              <span>Ministerio nacional · ACAD Costa Rica</span>
            </div>

            <div className="acad-ker-hero-copy">
              <span className="acad-ker-eyebrow acad-ker-eyebrow--light">Fe · Aventura · Servicio</span>
              <h1>
                Exploradores
                <span>del Rey</span>
              </h1>
              <p>
                Formando niños y jóvenes con carácter, habilidades y una fe firme para servir a
                Cristo en cada etapa de la vida.
              </p>
            </div>

            <div className="acad-ker-hero-bottom">
              <a className="acad-ker-button" href="#proposito">
                Descubrir el programa <ArrowDown aria-hidden="true" />
              </a>
              <div className="acad-ker-hero-tags">
                <span>Discipulado</span>
                <span>Campismo</span>
                <span>Liderazgo</span>
              </div>
            </div>
          </div>

          <span className="acad-ker-hero-word" aria-hidden="true">AVANZA</span>
        </section>

        <div className="acad-ker-marquee" aria-label="Valores de Exploradores del Rey">
          <div>
            <span>Listos</span><i />
            <span>Preparados</span><i />
            <span>Dispuestos</span><i />
            <span>Listos</span><i />
            <span>Preparados</span><i />
            <span>Dispuestos</span><i />
          </div>
        </div>

        <section className="acad-ker-purpose" id="proposito">
          <div className="acad-ker-shell acad-ker-purpose-grid">
            <div className="acad-ker-purpose-title">
              <span className="acad-ker-eyebrow">Nuestro propósito</span>
              <h2>Una aventura que forma toda la vida.</h2>
            </div>
            <div className="acad-ker-purpose-copy">
              <p>
                Exploradores del Rey es un ministerio de discipulado basado en relaciones,
                actividades prácticas y experiencias al aire libre. Cada encuentro ayuda a los
                muchachos a conocer a Jesús, descubrir sus capacidades y crecer junto a mentores
                que modelan una vida de servicio.
              </p>
              <p>
                No se trata solamente de aprender destrezas. Buscamos desarrollar integralmente a
                cada participante para que viva con <strong>fe, carácter, propósito y valentía.</strong>
              </p>
              <div className="acad-ker-purpose-quote">
                <Target aria-hidden="true" />
                <span>Desarrollando al hombre total para Cristo.</span>
              </div>
            </div>
          </div>
        </section>

        <section className="acad-ker-growth" id="formacion">
          <div className="acad-ker-shell">
            <header className="acad-ker-section-head acad-ker-section-head--dark">
              <span className="acad-ker-eyebrow acad-ker-eyebrow--light">Formación integral</span>
              <div>
                <h2>Cuatro dimensiones.<br />Un carácter firme.</h2>
                <p>
                  Un programa equilibrado que acompaña el crecimiento espiritual, físico, mental
                  y social de cada explorador.
                </p>
              </div>
            </header>

            <div className="acad-ker-growth-grid">
              {growthPillars.map((pillar) => {
                const Icon = pillar.icon;

                return (
                  <article className="acad-ker-growth-card" key={pillar.number}>
                    <div className="acad-ker-growth-top">
                      <span>{pillar.number}</span>
                      <i><Icon aria-hidden="true" /></i>
                    </div>
                    <small>{pillar.subtitle}</small>
                    <h3>{pillar.title}</h3>
                    <p>{pillar.description}</p>
                  </article>
                );
              })}
            </div>
          </div>
        </section>

        <section className="acad-ker-routes" id="rutas">
          <div className="acad-ker-shell">
            <header className="acad-ker-section-head">
              <span className="acad-ker-eyebrow">Una ruta para cada edad</span>
              <div>
                <h2>Crecer paso a paso.<br />Avanzar con propósito.</h2>
                <p>
                  Cada etapa utiliza actividades y desafíos adecuados para acompañar la madurez de
                  los participantes sin perder la alegría de descubrir.
                </p>
              </div>
            </header>

            <div className="acad-ker-route-grid">
              {ageRoutes.map((route) => (
                <article className="acad-ker-route-card" key={route.number}>
                  <div className="acad-ker-route-media">
                    <img src={route.image} alt={route.alt} loading="lazy" decoding="async" />
                    <span>{route.ages}</span>
                  </div>
                  <div className="acad-ker-route-copy">
                    <div>
                      <small>{route.number} · {route.motto}</small>
                      <h3>{route.title}</h3>
                    </div>
                    <p>{route.description}</p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="acad-ker-experiences" id="experiencias">
          <div className="acad-ker-shell acad-ker-experiences-layout">
            <div className="acad-ker-experiences-media">
              <img
                src={assetUrl("youth_leadership_training.png")}
                alt="Jóvenes desarrollando liderazgo y trabajo en equipo"
                loading="lazy"
                decoding="async"
              />
              <span className="acad-ker-photo-label"><Footprints aria-hidden="true" /> Aprender haciendo</span>
            </div>

            <div className="acad-ker-experiences-content">
              <span className="acad-ker-eyebrow">Experiencias que transforman</span>
              <h2>La fe se aprende caminando.</h2>
              <div className="acad-ker-experience-list">
                {experiences.map((experience, index) => {
                  const Icon = experience.icon;

                  return (
                    <article key={experience.title}>
                      <span>{String(index + 1).padStart(2, "0")}</span>
                      <i><Icon aria-hidden="true" /></i>
                      <div>
                        <h3>{experience.title}</h3>
                        <p>{experience.description}</p>
                      </div>
                    </article>
                  );
                })}
              </div>
            </div>
          </div>
        </section>

        <section className="acad-ker-commitment" data-nav="dark">
          <div className="acad-ker-shell acad-ker-commitment-grid">
            <div className="acad-ker-commitment-copy">
              <span className="acad-ker-eyebrow acad-ker-eyebrow--light">Hay un lugar para participar</span>
              <h2>Inicia o fortalece un destacamento en tu iglesia.</h2>
              <p>
                Conecta con la coordinación para conocer el programa, recibir orientación y dar el
                siguiente paso junto a tu congregación.
              </p>
              <a className="acad-ker-button" href={homeUrl("contacto/?tema=ministerios#contacto-formulario")}>
                Contactar la coordinación <ArrowUpRight aria-hidden="true" />
              </a>
            </div>
            <div className="acad-ker-commitment-media">
              <img
                src={assetUrl("national_scouts_camp_explorers.png")}
                alt="Campamento de Exploradores del Rey con tiendas y actividades al aire libre"
                loading="lazy"
                decoding="async"
              />
              <div className="acad-ker-commitment-badge">
                <Flame aria-hidden="true" />
                <span>Aventura con propósito</span>
              </div>
            </div>
          </div>
        </section>

        <section className="acad-ker-final">
          <div className="acad-ker-shell acad-ker-final-inner">
            <span><Sparkles aria-hidden="true" /></span>
            <div>
              <small>Fe que se pone en acción</small>
              <h2>Listos para servir. Preparados para liderar. Dispuestos para avanzar.</h2>
            </div>
            <a href="#rutas" aria-label="Conocer las rutas por edades">
              <ArrowUpRight aria-hidden="true" />
            </a>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
